import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Add user dialog
    const [addOpen, setAddOpen] = useState(false);
    const [addForm, setAddForm] = useState({ username: "", password: "", confirm: "" });
    const [addSaving, setAddSaving] = useState(false);

    // Change password dialog
    const [pwOpen, setPwOpen] = useState(false);
    const [pwUser, setPwUser] = useState(null);
    const [pwForm, setPwForm] = useState({ password: "", confirm: "" });
    const [pwSaving, setPwSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users");
            setUsers(data);
        } catch {
            toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const addUser = async () => {
        if (!addForm.username.trim() || !addForm.password) {
            toast.error("Username and password are required");
            return;
        }
        if (addForm.password !== addForm.confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setAddSaving(true);
        try {
            await api.post("/admin/users", { username: addForm.username.trim(), password: addForm.password });
            toast.success("User created");
            setAddOpen(false);
            setAddForm({ username: "", password: "", confirm: "" });
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create user");
        } finally {
            setAddSaving(false);
        }
    };

    const deleteUser = async (u) => {
        if (!window.confirm(`Delete user "${u.username}"?`)) return;
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("User deleted");
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not delete user");
        }
    };

    const openChangePw = (u) => {
        setPwUser(u);
        setPwForm({ password: "", confirm: "" });
        setPwOpen(true);
    };

    const changePassword = async () => {
        if (!pwForm.password) {
            toast.error("New password is required");
            return;
        }
        if (pwForm.password !== pwForm.confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setPwSaving(true);
        try {
            await api.put(`/admin/users/${pwUser.id}/password`, { new_password: pwForm.password });
            toast.success("Password changed");
            setPwOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not change password");
        } finally {
            setPwSaving(false);
        }
    };

    return (
        <section>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-serif text-3xl">Admin Users</h2>
                <Button
                    onClick={() => { setAddForm({ username: "", password: "", confirm: "" }); setAddOpen(true); }}
                    className="rounded-full bg-primary hover:bg-primary-hover"
                >
                    <Plus className="h-4 w-4 mr-1" />
                    New user
                </Button>
            </div>

            {loading ? (
                <p className="text-muted-foreground">Loading…</p>
            ) : users.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground border border-dashed border-border/60 rounded-2xl">
                    No admin users found.
                </div>
            ) : (
                <ul className="space-y-2">
                    {users.map((u) => (
                        <li
                            key={u.id}
                            className="flex items-center justify-between bg-card border border-border/60 rounded-2xl px-5 py-4"
                        >
                            <div>
                                <p className="font-medium">{u.username}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Added {new Date(u.created_at).toLocaleDateString()}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openChangePw(u)}
                                    className="rounded-full"
                                >
                                    <KeyRound className="h-3 w-3 mr-1" />
                                    Change password
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteUser(u)}
                                    className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {/* Add user dialog */}
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">New admin user</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Username</Label>
                            <Input
                                value={addForm.username}
                                onChange={(e) => setAddForm({ ...addForm, username: e.target.value })}
                                className="mt-1 rounded-xl"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Password</Label>
                            <Input
                                type="password"
                                value={addForm.password}
                                onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                                className="mt-1 rounded-xl"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Confirm password</Label>
                            <Input
                                type="password"
                                value={addForm.confirm}
                                onChange={(e) => setAddForm({ ...addForm, confirm: e.target.value })}
                                className="mt-1 rounded-xl"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={addUser} disabled={addSaving} className="rounded-full bg-primary hover:bg-primary-hover">
                            {addSaving ? "Creating…" : "Create user"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Change password dialog */}
            <Dialog open={pwOpen} onOpenChange={setPwOpen}>
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl">Change password</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground -mt-2">
                        Setting new password for <span className="font-medium text-foreground">{pwUser?.username}</span>
                    </p>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">New password</Label>
                            <Input
                                type="password"
                                value={pwForm.password}
                                onChange={(e) => setPwForm({ ...pwForm, password: e.target.value })}
                                className="mt-1 rounded-xl"
                                autoFocus
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em]">Confirm password</Label>
                            <Input
                                type="password"
                                value={pwForm.confirm}
                                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                                className="mt-1 rounded-xl"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button onClick={changePassword} disabled={pwSaving} className="rounded-full bg-primary hover:bg-primary-hover">
                            {pwSaving ? "Saving…" : "Save password"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </section>
    );
}
